import { useLayoutEffect } from 'react';
import type { RefObject } from 'react';

export function getAutoResizeMaxHeight(el: HTMLTextAreaElement, maxRows: number): number {
  const styles = getComputedStyle(el);
  const lineHeight = Number.parseFloat(styles.lineHeight) || 20;
  const verticalPadding =
    Number.parseFloat(styles.paddingTop) + Number.parseFloat(styles.paddingBottom) || 0;
  const verticalBorder =
    Number.parseFloat(styles.borderTopWidth) + Number.parseFloat(styles.borderBottomWidth) || 0;
  return lineHeight * maxRows + verticalPadding + verticalBorder;
}

export function getAutoResizeState(scrollHeight: number, maxHeight: number) {
  return {
    height: Math.min(scrollHeight, maxHeight),
    overflowY: scrollHeight > maxHeight ? 'auto' : 'hidden',
  } as const;
}

/** Grow a textarea with its content up to `maxRows`, then scroll internally. */
export function useAutoResize(
  ref: RefObject<HTMLTextAreaElement | null>,
  value: string,
  maxRows = 9,
): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = getAutoResizeMaxHeight(el, maxRows);
    const next = getAutoResizeState(el.scrollHeight, max);
    el.style.height = `${next.height}px`;
    el.style.overflowY = next.overflowY;
  }, [ref, value, maxRows]);
}
